import { useState, useEffect, useCallback, useRef } from 'react'
import { marketingApi } from '../api/marketing'
import Boton from '../components/Boton'
import { useAuth } from '../context/AuthContext'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * MARKETING → Regalos empresariales (Martin, 26 oct 2026 —
 * docs/diseno-campana-regalos-empresariales.md): la secuencia de 10 mails de
 * la campaña a los compradores de regalos y a los potenciales clientes.
 * Arriba los números confiables (sin aperturas), día por día y cada modelo
 * (RegalosMedicionService, Martin 3 nov 2026); después las tandas (quién entró y
 * cuántos quedan), las herramientas (ver cada mail, ensayo a mi casilla,
 * marcar que alguien respondió) y el listado de personas.
 */

const VISTAS = [
  { id: '', label: 'Todos' },
  { id: 'activa', label: 'Recibiendo mails' },
  { id: 'compro', label: 'Compraron' },
  { id: 'respondio', label: 'Respondieron' },
  { id: 'terminada', label: 'Terminaron' },
  { id: 'cortada', label: 'Cortados' },
  { id: 'baja', label: 'Bajas' },
  { id: 'control', label: 'Grupo de control' },
]

const ESTADO = {
  activa: { label: 'Recibiendo mails', bg: colors.infoBg, fg: colors.info },
  compro: { label: 'Compró', bg: colors.okBg, fg: colors.ok },
  respondio: { label: 'Respondió', bg: colors.okBg, fg: colors.ok },
  terminada: { label: 'Terminó', bg: colors.bgSoft, fg: colors.textSub },
  cortada: { label: 'Cortado', bg: colors.warnBg, fg: colors.warn },
  baja: { label: 'Se dio de baja', bg: colors.errBg, fg: colors.err },
  control: { label: 'Control', bg: colors.bgSoft, fg: colors.textSub },
}

const BASES = { premium: 'Premium', estuches: 'Estuches con vino', cajas: 'Cajas Navideñas', general: 'General' }
const ORIGENES = { cliente: 'Cliente', prospecto: 'Potencial cliente', linkedin: 'LinkedIn' }

/** clientes-ola-1 / clientes-ola-2 / prospectos-N / linkedin-AAAA-MM-DD → texto para la persona. */
const tandaLabel = (t) => {
  if (t === 'clientes-ola-1') return 'Clientes desde 2024'
  if (t === 'clientes-ola-2') return 'Resto de los clientes'
  if (/^linkedin-\d{4}-\d{2}-\d{2}$/.test(t || '')) return `LinkedIn · ${fmtFecha(t.slice(9))}`
  const m = /^prospectos-(\d+)$/.exec(t || '')
  return m ? `Potenciales · paquete ${m[1]}` : t
}

const ORDEN_BASE = ['premium', 'estuches', 'cajas', 'general']
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
const diaSemana = (f) => DIAS[new Date(`${f}T12:00:00`).getDay()]
const cero = (n) => (n ? fmtMonto(n, 0) : '—')
/** "Clientes: mail 3 (643), mail 4 (2.839) · Potenciales: mail 1 (1.724)" */
const queSalio = (modelos = []) => ['cliente', 'potencial']
  .map(q => {
    const m = modelos.filter(x => x.quien === q)
    return m.length ? `${q === 'cliente' ? 'Clientes' : 'Potenciales'}: ${m.map(x => `mail ${x.numero} (${fmtMonto(x.enviados, 0)})`).join(', ')}` : null
  })
  .filter(Boolean).join(' · ') || '—'

const pct = (a, b) => (b ? `${String(Math.round((1000 * a) / b) / 10).replace('.', ',')} %` : '—')
const num = (n) => fmtMonto(n ?? 0, 0)

export default function MktRegalosEmpresarialesPage() {
  const { hasAnyRole } = useAuth()
  const esAdmin = hasAnyRole(['administrador'])
  const [vista, setVista] = useStoredState('lgr.filtros.regalos-emp', '')
  const [origen, setOrigen] = useStoredState('lgr.filtros.regalos-emp-origen', '')
  const [base, setBase] = useStoredState('lgr.filtros.regalos-emp-base', '')
  const [q, setQ] = useState('')
  const [buscado, setBuscado] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [confirmar, setConfirmar] = useState(null) // { texto, accion }
  const [cupo, setCupo] = useState('')
  const [mailVista, setMailVista] = useState('mail-01-catalogo.html')
  const [htmlVista, setHtmlVista] = useState(null) // null = cargando · false = no se pudo
  const [respondio, setRespondio] = useState('')
  const [telPrueba, setTelPrueba] = useState('')
  const archivoLinkedin = useRef(null)

  const cargar = useCallback(async () => {
    try {
      const r = await marketingApi.regalosEmp({ estado: vista || undefined, origen: origen || undefined, base: base || undefined, q: buscado || undefined })
      setData(r.data)
      setError(false)
    } catch { setError(true) }
  }, [vista, origen, base, buscado])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 60000)

  useEffect(() => {
    let vivo = true
    setHtmlVista(null)
    marketingApi.regalosEmpMail(mailVista).then(({ data: h }) => { if (vivo) setHtmlVista(h) }).catch(() => { if (vivo) setHtmlVista(false) })
    return () => { vivo = false }
  }, [mailVista])

  const accion = async (fn, textoOk) => {
    setOcupado(true); setAviso(null)
    try {
      const { data: d } = await fn()
      setConfirmar(null)
      setAviso({ ok: true, texto: d?.mensaje || textoOk })
      cargar()
      return true
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo completar.' })
      return false
    } finally { setOcupado(false) }
  }

  const r = data?.resumen
  const disp = data?.disponibles ?? {}
  const activa = !!r?.activo
  const dias = data?.medicion?.por_dia ?? []
  const modelos = data?.medicion?.por_modelo ?? []
  const waFila = modelos.find(m => m.base === 'whatsapp')
  const mailsFilas = modelos.filter(m => m.base !== 'whatsapp')
    .sort((a, b) => (a.quien === b.quien ? 0 : a.quien === 'cliente' ? -1 : 1) || a.numero - b.numero || ORDEN_BASE.indexOf(a.base) - ORDEN_BASE.indexOf(b.base))
  const suma = (lista, k) => lista.reduce((a, x) => a + (x[k] ?? 0), 0)
  const mailsTotal = suma(dias, 'mails_clientes') + suma(dias, 'mails_potenciales')
  const respMail = suma(mailsFilas, 'respuestas')
  const respWa = waFila?.respuestas ?? 0
  const comprasTotal = suma(dias, 'compras')
  const montoTotal = suma(dias, 'monto')
  const control = r?.compras?.control ?? { inscriptos: 0, compraron: 0 }
  const filas = data?.data ?? []

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Regalos empresariales</h2>
          <p style={st.sub}>
            Diez mails a cada comprador de regalos y a cada potencial cliente, en tandas de 10 a 18 h. El primero
            presenta el catálogo y los siguientes son de su línea (premium, estuches, cajas o general). Sale de la
            campaña quien compra un regalo, responde o se da de baja. Un mail no sale si alguno de sus regalos dejó de
            venderse o cambió de precio.
          </p>
        </div>
        <div style={st.acciones}>
          <span style={{ ...sx.badge, ...(activa ? { background: colors.okBg, color: colors.ok } : { background: colors.bgSoft, color: colors.textSub }) }}>
            {activa ? 'Prendida' : r?.arranca ? `Arranca el ${r.arranca}` : 'Apagada'}
          </span>
          {esAdmin && !confirmar && (
            <Boton variante={activa ? 'secondary' : 'primary'} disabled={ocupado}
              onClick={() => setConfirmar({
                texto: activa
                  ? '¿Apagar la campaña? No sale ningún mail más hasta que la vuelvas a prender.'
                  : `¿Prender la campaña? Los mails salen de 10 a 18 h, hasta ${num(r?.cupo_diario)} por día, a las personas de las tandas ya sumadas.`,
                accion: () => accion(() => marketingApi.regalosEmpConfigurar({ activo: !activa }), activa ? 'La campaña quedó apagada.' : 'La campaña quedó prendida.'),
              })}>
              {activa ? 'Apagar' : 'Prender'}
            </Boton>
          )}
        </div>
      </div>

      {confirmar && (
        <div style={st.confirmar}>
          <span>{confirmar.texto}</span>
          <Boton variante="primary" sm onClick={confirmar.accion} disabled={ocupado}>Sí, confirmar</Boton>
          <Boton variante="ghost" sm onClick={() => setConfirmar(null)}>Cancelar</Boton>
        </div>
      )}
      {aviso && <p style={{ ...st.aviso, color: aviso.ok ? colors.ok : colors.err }}>{aviso.texto}</p>}
      {error && <p style={st.error}>No se pudo actualizar. Se reintenta solo en un minuto.</p>}

      {/* ── Los números (solo datos confiables: sin aperturas) ── */}
      <div style={st.kpis}>
        <Kpi label="Mails enviados" valor={num(mailsTotal)} detalle={`${num(r?.enviados_hoy)} hoy · cupo ${num(r?.cupo_diario)} por día`} />
        <Kpi label="WhatsApp enviados" valor={num(waFila?.enviados)} detalle={`${num(r?.whatsapp?.por_mandar)} por mandar · ${num(r?.whatsapp?.fallidos)} fallidos`} />
        <Kpi label="Respondieron" valor={num(respMail + respWa)} detalle={`${num(respWa)} por WhatsApp · ${num(respMail)} después de un mail`} />
        <Kpi destacado label="Compraron" valor={num(comprasTotal)}
          detalle={`$ ${num(montoTotal)} · sin mails (control): ${num(control.compraron)} de ${num(control.inscriptos)}`} />
        <Kpi label="Bajas y rebotes" valor={num(suma(dias, 'bajas'))} detalle={`se dieron de baja · ${num(suma(dias, 'rebotes'))} rebotaron`} />
      </div>
      <p style={st.nota}>
        No se muestran aperturas: Apple marca como abierto todo mail que llega a un iPhone y los antivirus de las empresas
        los abren solos, así que no sirven para comparar. Un click cuenta solo si es de una persona (llegó {Math.round((data?.medicion?.criterio?.segundos_click ?? 120) / 60)} minutos
        o más después del mail y no en ráfaga, como revisan los links los antivirus). Cada respuesta y cada compra se le cuenta al último
        contacto que la persona recibió (un mail o el WhatsApp). El grupo de control no entra en estas cuentas.
      </p>

      {/* ── Por día ── */}
      <div style={st.panel}>
        <div style={st.kpiLabel}>Día por día</div>
        <table style={{ ...sx.table, marginTop: space.sm }}>
          <thead>
            <tr>
              <th style={st.thChico}>Día</th>
              <th style={st.thNum}>Mails a clientes</th>
              <th style={st.thNum}>Mails a potenciales</th>
              <th style={st.thChico}>Qué mail salió</th>
              <th style={st.thNum}>WhatsApp</th>
              <th style={st.thNum}>Clicks de personas</th>
              <th style={st.thNum}>Respondieron</th>
              <th style={st.thNum}>Compraron</th>
              <th style={st.thNum}>Monto</th>
              <th style={st.thNum}>Bajas</th>
              <th style={st.thNum}>Rebotes</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={11} style={st.vacioChico}>Cargando…</td></tr>}
            {data && dias.length === 0 && <tr><td colSpan={11} style={st.vacioChico}>Todavía no salió ningún mail.</td></tr>}
            {dias.map(d => (
              <tr key={d.fecha}>
                <td style={{ ...st.tdChico, ...sx.mono }}>{diaSemana(d.fecha)} {fmtFecha(d.fecha)}</td>
                <td style={st.tdNum}>{cero(d.mails_clientes)}</td>
                <td style={st.tdNum}>{cero(d.mails_potenciales)}</td>
                <td style={st.tdTexto}>{queSalio(d.modelos)}</td>
                <td style={st.tdNum}>{cero(d.whatsapp)}</td>
                <td style={st.tdNum}>{cero(d.clicks_personas)}</td>
                <td style={st.tdNum}>{cero(d.respuestas)}</td>
                <td style={{ ...st.tdNum, ...(d.compras ? st.positivo : {}) }}>{cero(d.compras)}</td>
                <td style={st.tdNum}>{d.monto ? `$ ${num(d.monto)}` : '—'}</td>
                <td style={st.tdNum}>{cero(d.bajas)}</td>
                <td style={st.tdNum}>{cero(d.rebotes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Por modelo ── */}
      <div style={st.panel}>
        <div style={st.kpiLabel}>Cada modelo</div>
        <table style={{ ...sx.table, marginTop: space.sm }}>
          <thead>
            <tr>
              <th style={st.thChico}>Modelo</th>
              <th style={st.thChico}>A quién</th>
              <th style={st.thChico}>Desde</th>
              <th style={st.thNum}>Enviados</th>
              <th style={st.thNum}>Clicks de personas</th>
              <th style={st.thNum}>Respondieron</th>
              <th style={st.thNum}>Compraron</th>
              <th style={st.thNum}>Monto</th>
              <th style={st.thNum}>Bajas</th>
              <th style={st.thNum}>Rebotes</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={10} style={st.vacioChico}>Cargando…</td></tr>}
            {[...mailsFilas, ...(waFila ? [waFila] : [])].map(m => (
              <tr key={`${m.quien}-${m.base}-${m.numero}`} style={st.filaModelo}>
                <td style={st.tdModelo}>
                  <div style={st.nombre}>{m.base === 'whatsapp' ? 'WhatsApp' : `Mail ${m.numero} · ${BASES[m.base] ?? m.base}`}</div>
                  <div style={st.subLinea}>{m.asunto}</div>
                </td>
                <td style={st.tdChico}>{m.quien === 'cliente' ? 'Clientes' : 'Potenciales'}</td>
                <td style={{ ...st.tdChico, ...sx.mono }}>{m.desde ? fmtFecha(m.desde) : '—'}</td>
                <td style={st.tdNum}>{num(m.enviados)}</td>
                <Celda n={m.clicks_personas} de={m.enviados} />
                <Celda n={m.respuestas} de={m.enviados} />
                <Celda n={m.compras} de={m.enviados} positivo />
                <td style={st.tdNum}>{m.monto ? `$ ${num(m.monto)}` : '—'}</td>
                <Celda n={m.bajas} de={m.enviados} />
                <Celda n={m.rebotes} de={m.enviados} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Tandas ── */}
      <div style={st.panel}>
        <div style={st.panelHead}>
          <div style={st.kpiLabel}>Tandas</div>
          {esAdmin && (
            <div style={st.cupoEdit}>
              <span style={st.ayuda}>Cupo por día</span>
              <input style={st.cupoInput} inputMode="numeric" placeholder={String(r?.cupo_diario ?? '')} value={cupo}
                onChange={e => setCupo(e.target.value.replace(/\D/g, ''))} />
              <Boton variante="secondary" sm disabled={ocupado || cupo === ''}
                onClick={() => accion(() => marketingApi.regalosEmpConfigurar({ cupo_diario: Number(cupo) }), 'Cupo guardado.').then(ok => { if (ok) setCupo('') })}>
                Guardar
              </Boton>
            </div>
          )}
        </div>
        <table style={{ ...sx.table, marginTop: space.sm }}>
          <thead>
            <tr>
              <th style={st.thChico}>Tanda</th>
              <th style={st.thChico}>Quiénes</th>
              <th style={st.thChico}>Personas</th>
              <th style={st.thChico}>Desde</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={4} style={st.vacioChico}>Cargando…</td></tr>}
            {data && (r?.tandas ?? []).length === 0 && <tr><td colSpan={4} style={st.vacioChico}>Todavía no entró ninguna tanda.</td></tr>}
            {(r?.tandas ?? []).map(t => (
              <tr key={t.tanda}>
                <td style={st.tdChico}>{tandaLabel(t.tanda)}</td>
                <td style={st.tdChico}>{ORIGENES[t.origen] ?? t.origen}</td>
                <td style={{ ...st.tdChico, ...sx.mono }}>{num(t.n)}</td>
                <td style={{ ...st.tdChico, ...sx.mono }}>{fmtFecha(t.desde)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {esAdmin && (
          <div style={st.botonera}>
            {[
              { tanda: 'ola1', label: `Sumar clientes que compraron desde 2024 (${num(disp.ola1)})`, n: disp.ola1 },
              { tanda: 'ola2', label: `Sumar el resto de los clientes (${num(disp.ola2)})`, n: disp.ola2 },
              { tanda: 'prospectos', label: `Sumar 2.000 potenciales (${num(disp.prospectos)})`, n: disp.prospectos },
            ].map(b => (
              <Boton key={b.tanda} variante="secondary" sm disabled={ocupado || !b.n}
                onClick={() => setConfirmar({
                  texto: `¿${b.label}? Entran a la secuencia y reciben el mail 1 en la próxima tanda horaria${activa ? '' : ' cuando prendas la campaña'}.`,
                  accion: () => accion(() => marketingApi.regalosEmpInscribir(b.tanda, b.tanda === 'prospectos' ? 2000 : undefined), 'Tanda sumada.'),
                })}>
                {b.label}
              </Boton>
            ))}
          </div>
        )}
        <div style={st.botonera}>
          <div style={{ ...st.ayuda, display: 'flex', alignItems: 'center', gap: space.sm }}>
            <Boton variante="secondary" sm disabled={ocupado} onClick={() => archivoLinkedin.current?.click()}>
              Subir contactos de LinkedIn (CSV)
            </Boton>
            <input ref={archivoLinkedin} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={e => {
                const archivo = e.target.files?.[0]
                e.target.value = ''
                if (archivo) accion(() => marketingApi.regalosEmpLinkedin(archivo), 'Contactos importados.')
              }} />
            El archivo que se descarga del formulario en Campaign Manager. Cada contacto nuevo le llega a ventas@ y entra a los mails; subir dos veces el mismo no duplica.
          </div>
        </div>
      </div>

      {/* ── Los mails ── */}
      <div style={st.plantillas}>
        <div style={st.plantillasCtl}>
          <div style={st.kpiLabel}>Los mails</div>
          <select style={st.select} value={mailVista} onChange={e => setMailVista(e.target.value)}>
            {(data?.mails ?? []).map(m => <option key={m.archivo} value={m.archivo}>{m.archivo.replace('.html', '')} · {m.asunto}</option>)}
          </select>
          <Boton variante="secondary" sm disabled={ocupado}
            onClick={() => accion(() => marketingApi.regalosEmpEnsayo(mailVista), 'Ensayo enviado a tu casilla.')}>Mandarme este mail</Boton>
          <Boton variante="secondary" sm disabled={ocupado}
            onClick={() => accion(() => marketingApi.regalosEmpEnsayo(), 'Ensayo enviado a tu casilla.')}>Mandarme los {data?.mails?.length ?? ''}</Boton>
          <p style={st.ayuda}>El ensayo llega a tu casilla con el asunto marcado [PRUEBA]. A ningún cliente le llega nada.</p>
          <div style={st.kpiLabel}>WhatsApp de prueba</div>
          <div style={st.respondioForm}>
            <input style={st.clienteInput} inputMode="tel" placeholder="tu celular, ej. 11 4145-3198" value={telPrueba} onChange={e => setTelPrueba(e.target.value)} />
            <Boton variante="send" sm disabled={ocupado || !telPrueba}
              onClick={() => accion(() => marketingApi.regalosEmpWhatsappPrueba(telPrueba), 'WhatsApp enviado.')}>Mandarme el WhatsApp</Boton>
          </div>
          <div style={st.kpiLabel}>Respondió</div>
          <div style={st.respondioForm}>
            <input style={st.clienteInput} type="email" placeholder="mail de quien respondió" value={respondio} onChange={e => setRespondio(e.target.value)} />
            <Boton variante="success" sm disabled={ocupado || !respondio}
              onClick={() => accion(() => marketingApi.regalosEmpRespondio(respondio), 'Marcado como respondió.').then(ok => { if (ok) setRespondio('') })}>✓ Marcar</Boton>
          </div>
          <p style={st.ayuda}>Deja de recibir la campaña: lo atiende Ventas.</p>
        </div>
        <div style={st.previewCol}>
          <div style={st.previewWrap}>
            {htmlVista ? <iframe title="Vista previa" style={st.iframe} srcDoc={htmlVista} sandbox="" />
              : <p style={st.vacioChico}>{htmlVista === false ? 'No se pudo cargar este mail.' : 'Cargando el mail…'}</p>}
          </div>
        </div>
      </div>

      {/* ── Las personas ── */}
      <div style={st.seccion}><div style={st.kpiLabel}>Las personas</div></div>
      <div style={sx.filterBar}>
        {VISTAS.map(v => (
          <button key={v.id} type="button" onClick={() => setVista(v.id)}
            style={{ ...sx.pill, ...(vista === v.id ? sx.pillActive : {}) }}>{v.label}</button>
        ))}
        <select value={origen} onChange={e => setOrigen(e.target.value)} style={st.select}>
          <option value="">Clientes y potenciales</option>
          {Object.entries(ORIGENES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={base} onChange={e => setBase(e.target.value)} style={st.select}>
          <option value="">Todas las líneas</option>
          {Object.entries(BASES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <form onSubmit={e => { e.preventDefault(); setBuscado(q.trim()) }} style={st.clienteForm}>
          <input style={st.clienteInput} placeholder="Buscar por mail" value={q} onChange={e => setQ(e.target.value)} />
          <Boton variante="secondary" sm type="submit">Ver</Boton>
        </form>
      </div>

      <div style={st.tableWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Persona</th>
              <th style={sx.th}>Línea</th>
              <th style={sx.th}>Tanda</th>
              <th style={sx.th}>Mails</th>
              <th style={sx.th}>Próximo</th>
              <th style={sx.th}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={6} style={st.vacio}>Cargando…</td></tr>}
            {data && filas.length === 0 && <tr><td colSpan={6} style={st.vacio}>No hay personas con este filtro.</td></tr>}
            {filas.map(f => {
              const e = ESTADO[f.estado] ?? { label: f.estado, bg: colors.bgSoft, fg: colors.textSub }
              return (
                <tr key={f.id} style={st.fila}>
                  <td style={st.td}>
                    <div style={st.nombre}>{f.nombre || f.email}</div>
                    <div style={st.subLinea}>{f.codigo ? <span style={sx.mono}>{f.codigo} · </span> : null}{f.email}</div>
                  </td>
                  <td style={st.td}>
                    {BASES[f.base] ?? f.base}
                    <div style={st.subLinea}>{ORIGENES[f.origen] ?? f.origen}</div>
                  </td>
                  <td style={st.td}>{tandaLabel(f.tanda)}</td>
                  <td style={{ ...st.td, ...sx.mono }}>{f.grupo_control ? '—' : `${f.mails_enviados}/10`}</td>
                  <td style={{ ...st.td, ...sx.mono }}>{f.proximo_mail_el ? fmtFecha(f.proximo_mail_el) : '—'}</td>
                  <td style={st.td}>
                    <span style={{ ...sx.badge, background: e.bg, color: e.fg }}>{e.label}</span>
                    {(f.motivo || f.compro_por) && <div style={st.subLinea}>{f.compro_por || f.motivo}</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Cantidad con su porcentaje sobre los enviados debajo. null = no aplica (WhatsApp sin clicks ni bajas). */
function Celda({ n, de, positivo = false }) {
  if (n === null || n === undefined) return <td style={st.tdNum}>—</td>
  return (
    <td style={{ ...st.tdNum, ...(positivo && n ? st.positivo : {}) }}>
      <div>{n ? fmtMonto(n, 0) : '—'}</div>
      {n > 0 && <div style={st.subNum}>{pct(n, de)}</div>}
    </td>
  )
}

function Kpi({ label, valor, detalle, destacado = false }) {
  return (
    <div style={{ ...st.kpi, ...(destacado ? st.kpiDestacado : {}) }}>
      <div style={st.kpiLabel}>{label}</div>
      <div style={{ ...st.kpiValor, ...(destacado ? { color: colors.ok } : {}) }}>{valor}</div>
      <div style={st.kpiDetalle}>{detalle}</div>
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.lg, marginBottom: space.lg, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 0`, fontSize: text.sec, color: colors.textSec, maxWidth: 760 },
  acciones: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' },
  confirmar: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', padding: `${space.md}px ${space.lg}px`, background: colors.warnBg, border: `1px solid ${colors.border}`, borderRadius: radius.md, marginBottom: space.md, fontSize: text.body },
  aviso: { fontSize: text.sec, margin: `0 0 ${space.md}px` },
  error: { fontSize: text.sec, color: colors.err, margin: `0 0 ${space.md}px` },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: space.md, marginBottom: space.lg },
  kpi: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, minWidth: 0 },
  kpiDestacado: { borderColor: colors.ok, background: colors.okSoft },
  kpiLabel: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  kpiValor: { ...sx.mono, fontSize: 22, fontWeight: 600, marginTop: space.xs, color: colors.text },
  kpiDetalle: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  panel: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, minWidth: 0, overflowX: 'auto' },
  panelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  botonera: { display: 'flex', gap: space.sm, flexWrap: 'wrap', marginTop: space.md },
  nota: { fontSize: text.sec, color: colors.textSec, margin: `-${space.sm}px 0 ${space.lg}px`, maxWidth: 900 },
  thNum: { ...sx.th, height: 28, fontSize: text.meta, padding: `0 ${space.sm}px`, textAlign: 'right' },
  tdNum: { ...sx.td, ...sx.mono, height: 30, fontSize: text.sec, whiteSpace: 'nowrap', padding: `${space.xs}px ${space.sm}px`, textAlign: 'right' },
  tdTexto: { ...sx.td, fontSize: text.sec, color: colors.textSec, padding: `${space.xs}px ${space.sm}px`, minWidth: 220 },
  tdModelo: { ...sx.td, padding: `${space.xs}px ${space.sm}px`, minWidth: 240 },
  filaModelo: { height: 48, borderBottom: `1px solid ${colors.borderSoft}` },
  subNum: { fontSize: text.meta, color: colors.textMeta },
  positivo: { color: colors.ok, fontWeight: 600 },
  thChico: { ...sx.th, height: 28, fontSize: text.meta, padding: `0 ${space.sm}px` },
  tdChico: { ...sx.td, height: 30, fontSize: text.sec, whiteSpace: 'nowrap', padding: `0 ${space.sm}px` },
  vacioChico: { ...sx.td, height: 40, fontSize: text.sec, color: colors.textMeta, textAlign: 'center' },
  seccion: { margin: `${space['2xl']}px 0 ${space.md}px`, paddingTop: space.md, borderTop: `1px solid ${colors.border}` },
  plantillas: { display: 'flex', flexWrap: 'wrap', gap: space.md, marginBottom: space.lg, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.lg },
  plantillasCtl: { flex: '1 1 220px', maxWidth: 300, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.md, alignItems: 'flex-start' },
  select: { ...sx.input, width: 'auto', maxWidth: '100%' },
  previewCol: { flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.sm },
  previewWrap: { minWidth: 0, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.md, overflow: 'hidden', minHeight: 420, background: colors.bgSoft },
  iframe: { width: '100%', height: 760, border: 0, display: 'block' },
  ayuda: { fontSize: text.sec, color: colors.textSec, margin: 0 },
  clienteForm: { display: 'flex', gap: space.sm, alignItems: 'center' },
  clienteInput: { ...sx.input, width: 170, minWidth: 0 },
  respondioForm: { display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%' },
  cupoEdit: { display: 'flex', gap: space.sm, alignItems: 'center' },
  cupoInput: { ...sx.input, width: 90 },
  tableWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflowX: 'auto' },
  fila: { height: 56, borderBottom: `1px solid ${colors.borderSoft}` },
  vacio: { ...sx.td, height: 64, textAlign: 'center', color: colors.textSec },
  td: { ...sx.td, padding: `${space.sm}px ${space.lg}px`, whiteSpace: 'nowrap' },
  nombre: { fontWeight: 500 },
  subLinea: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
}
